"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";

import type { WorkspacePage } from "~/workspaces/state/workspace-page";
import { Button } from "~/common/components/button";
import { Pathnames } from "~/common/utils/constants";
import { UserInfo } from "~/auth/components/user-info";
import { IconTeam } from "~/common/components/icons/team";
import { IconEdit } from "~/common/components/icons/edit";
import { IconGlobe } from "~/common/components/icons/globe";
import { useWorkspace } from "~/workspaces/hooks/use-workspace";
import { useWorkspacePageData } from "~/workspaces/queries/get-workspace-page-data-query";
import { useGetConversations } from "~/conversations/queries/get-conversations-query";
import { useGetWorkspacePages } from "~/workspaces/queries/get-workspace-pages-query";
import { WorkspaceSelectDropdown } from "~/workspaces/components/workspace-select-dropdown";
import { useIsAnyPromptMutating } from "~/conversations/mutations/add-conversation-message";
import { useIsConversationMutating } from "~/conversations/mutations/create-conversation-mutation";

import { MenuItem } from "./menu-item";
import { sideNavStyle } from "./navigation.style";
import { NavigationSection } from "./navigation-section";

function NavigationLeft({ className }: { className?: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { selectedWorkspace } = useWorkspace();

  // TODO: temporary solution for disabling new button, this should be done differently
  const isCreatingConversation = useIsConversationMutating({
    workspaceId: selectedWorkspace.id,
  });

  const isAnyPromptMutatingValue = useIsAnyPromptMutating({
    workspaceId: selectedWorkspace.id,
  });

  const isNewDisabled =
    isCreatingConversation ||
    isAnyPromptMutatingValue ||
    pathname.includes(Pathnames.NEW);

  function handleNewButtonClick() {
    const currentParams = new URLSearchParams(searchParams.toString());
    router.push(`/${selectedWorkspace.slug}${Pathnames.NEW}?${currentParams}`);
  }

  return (
    <aside className={sideNavStyle.container({ className })}>
      <div className="flex grow flex-col gap-16">
        <div className={sideNavStyle.workspaceDropdown()}>
          <WorkspaceSelectDropdown dataTestTag="workspace-selection-trigger" />
        </div>

        <nav className={sideNavStyle.navigationContent()}>
          <div className={sideNavStyle.actionButtons()}>
            <div />
            <Button
              onClick={handleNewButtonClick}
              variant="ghost"
              dataTestTag="create-new-chat"
              disabled={isNewDisabled}
            >
              <IconEdit />
              New
            </Button>
          </div>

          <div className={sideNavStyle.navigationSections()}>
            <ProjectDetailsSection />
            <ConversationSection />
          </div>

          <UserInfo selectedWorkspaceId={selectedWorkspace.id} />
        </nav>
      </div>
    </aside>
  );
}

function ProjectDetailsSection() {
  const { selectedWorkspace } = useWorkspace();
  const { data: pages } = useGetWorkspacePages({
    workspaceId: selectedWorkspace.id,
  });

  return (
    <NavigationSection
      label="Project Details"
      items={pages}
      renderItem={(page) => (
        <li key={page.id}>
          <MenuPageDetailsItem page={page} />
        </li>
      )}
      dataTestTag="project-details-list"
    />
  );
}

function MenuPageDetailsItem({ page }: { page: WorkspacePage }) {
  const pathname = usePathname();
  const { selectedWorkspace } = useWorkspace();

  function getPagePath(
    workspacePage: WorkspacePage,
  ): "/" | `/${string}/page/${string}` {
    if (selectedWorkspace) {
      return `/${selectedWorkspace.slug}${Pathnames.PAGE}/${workspacePage.href}`;
    }
    return `/`;
  }

  const pagePath = getPagePath(page);
  const isActive = pathname === pagePath;

  const { isFetching } = useWorkspacePageData(
    {
      workspaceId: selectedWorkspace?.id,
      pageId: page.id,
    },
    {
      enabled: isActive,
    },
  );

  function getPageIcon(icon: "globe" | "team" | undefined) {
    switch (icon) {
      case "team":
        return <IconTeam />;
      case "globe":
      default:
        return <IconGlobe />;
    }
  }

  return (
    <MenuItem
      isSingleLine
      href={getPagePath(page)}
      isActive={!!isActive}
      icon={getPageIcon(page.icon)}
      isLoading={isFetching}
    >
      <span className="flex-1">{page.title}</span>
    </MenuItem>
  );
}

function ConversationSection() {
  const pathname = usePathname();
  const { selectedWorkspace } = useWorkspace();
  const { data: conversations } = useGetConversations({
    workspaceId: selectedWorkspace.id,
  });

  function getConversationPath(
    conversationId: number,
  ): `/${string}/conversation/${string}` {
    return `/${selectedWorkspace.slug}${Pathnames.CONVERSATION}/${conversationId}`;
  }

  function isActiveDynamic(id?: number) {
    if (!id) return false;

    return pathname === getConversationPath(id);
  }

  if (!conversations?.length) {
    return null;
  }

  return (
    <NavigationSection
      label={conversations.length ? "Today" : "History"}
      items={conversations}
      renderItem={(item) => (
        <li
          key={item.id}
          data-test={`item-${item.title}`}
        >
          <MenuItem
            href={getConversationPath(item.id)}
            isActive={isActiveDynamic(item.id)}
            isSingleLine
          >
            {item.title}
          </MenuItem>
        </li>
      )}
      dataTestTag={`${conversations.length ? "today" : "history"}-list`}
    />
  );
}

export { NavigationLeft };
